import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { fetchAllEmployees } from '../../../store/slices/employeeManagementSlice';
import './BugDetailView.scss';

const BugDetailView = ({ bug, onClose, onStatusUpdate, onAssignBug, currentUserEmail }) => {
  const dispatch = useDispatch();
  const { employees, loading: loadingEmployees } = useSelector(state => state.employeeManagement);
  const [selectedAssignee, setSelectedAssignee] = useState('');
  const [showAssignSection, setShowAssignSection] = useState(false);
  const [isAssigning, setIsAssigning] = useState(false);
  
  // Fetch all employees from myeliners collection on component mount
  useEffect(() => {
    if (employees.length === 0) {
      dispatch(fetchAllEmployees());
    }
  }, [dispatch, employees.length]);
  
  if (!bug) return null;
  
  const isAssignedToCurrentUser = bug.assignedTo?.email === currentUserEmail;
  
  // Get available employees for assignment (excluding the reporter)
  const availableEmployees = employees.filter(emp => 
    emp.email !== bug.submittedBy?.email && emp.email // Ensure email exists
  );
  
  const handleAssignment = async () => {
    if (!selectedAssignee || !onAssignBug) return;
    
    const assignee = availableEmployees.find(emp => emp.email === selectedAssignee);
    if (!assignee) return;
    
    setIsAssigning(true);
    try {
      await onAssignBug(bug.id, {
        email: assignee.email,
        name: assignee.name
      });
      setShowAssignSection(false);
      setSelectedAssignee('');
    } catch (error) {
      console.error('Failed to assign bug:', error);
    } finally {
      setIsAssigning(false);
    }
  };
  
  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  const getSensitivityBadge = (sensitivity) => {
    const sensitivityConfig = {
      low: { color: 'success', text: 'Low' },
      medium: { color: 'warning', text: 'Medium' },
      high: { color: 'danger', text: 'High' },
      critical: { color: 'critical', text: 'Critical' }
    };
    
    const config = sensitivityConfig[sensitivity] || { color: 'secondary', text: sensitivity };
    return <span className={`sensitivity-badge ${config.color}`}>{config.text}</span>;
  };
  
  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: 'warning', text: 'Pending' },
      assigned: { color: 'info', text: 'Assigned' },
      in_progress: { color: 'primary', text: 'In Progress' },
      resolved: { color: 'success', text: 'Resolved' }
    };
    
    const config = statusConfig[status] || { color: 'secondary', text: status };
    return <span className={`status-badge ${config.color}`}>{config.text}</span>;
  };
  
  return (
    <div className="bug-detail-modal">
      <div className="bug-detail-header">
        <div className="header-content">
          <h3>{bug.title}</h3>
          <div className="bug-meta">
            <div className="meta-item">
              <span className="label">Status:</span>
              <span className="value">{getStatusBadge(bug.status)}</span>
            </div>
            <div className="meta-item">
              <span className="label">Sensitivity:</span>
              <span className="value">{getSensitivityBadge(bug.sensitivity)}</span>
            </div>
            <div className="meta-item">
              <span className="label">Game/App:</span>
              <span className="value">{bug.gameName}</span>
            </div>
          </div>
        </div>
        <button className="close-button" onClick={onClose}>&times;</button>
      </div>
      
      <div className="bug-detail-content">
        <div className="bug-description">
          <h4>Description</h4>
          <p>{bug.description || 'No description provided'}</p>
        </div>
        
        {bug.stepsToReproduce && (
          <div className="bug-steps">
            <h4>Steps to Reproduce</h4>
            <p>{bug.stepsToReproduce}</p>
          </div>
        )}
        
        {bug.expectedBehavior && (
          <div className="bug-expected">
            <h4>Expected Behavior</h4>
            <p>{bug.expectedBehavior}</p>
          </div>
        )}
        
        {bug.actualBehavior && (
          <div className="bug-actual">
            <h4>Actual Behavior</h4>
            <p>{bug.actualBehavior}</p>
          </div>
        )}
        
        {bug.deviceInfo && (
          <div className="bug-device">
            <h4>Device Information</h4>
            <p>{bug.deviceInfo}</p>
          </div>
        )}
        
        <div className="bug-info-grid">
          <div className="info-section">
            <h4>Submitted By</h4>
            <div className="user-info">
              <span className="user-name">{bug.submittedBy?.name || 'N/A'}</span>
              <span className="user-email">{bug.submittedBy?.email || 'N/A'}</span>
              <span className="date">on {formatDate(bug.submittedAt)}</span>
            </div>
          </div>
          
          <div className="info-section">
            <h4>Assigned To</h4>
            <div className="user-info">
              {bug.assignedTo ? (
                <>
                  <span className="user-name">{bug.assignedTo.name}</span>
                  <span className="user-email">{bug.assignedTo.email}</span>
                </>
              ) : (
                <span className="not-assigned">Not Assigned</span>
              )}
            </div>
            {!showAssignSection && (
              <button 
                className="assign-toggle-btn"
                onClick={() => setShowAssignSection(true)}
              >
                {bug.assignedTo ? 'Reassign' : 'Assign'}
              </button>
            )}
          </div>
          
          {bug.lastUpdated && (
            <div className="info-section">
              <h4>Last Updated</h4>
              <div className="update-info">
                <span className="date">{formatDate(bug.lastUpdated)}</span>
                {bug.updatedBy && (
                  <span className="updated-by">by {bug.updatedBy.name}</span>
                )}
              </div>
            </div>
          )}
        </div>
        
        {showAssignSection && (
          <div className="assignment-section">
            <h4>Assign Bug</h4>
            <div className="assignment-form">
              <div className="form-group">
                <label htmlFor="assignee">Select Assignee:</label>
                <select 
                  id="assignee"
                  value={selectedAssignee}
                  onChange={(e) => setSelectedAssignee(e.target.value)}
                  disabled={isAssigning || loadingEmployees}
                >
                  <option value="">
                    {loadingEmployees ? 'Loading employees...' : 'Choose an assignee...'}
                  </option>
                  {availableEmployees.map(employee => (
                    <option key={employee.email} value={employee.email}>
                      {employee.name} ({employee.email})
                    </option>
                  ))}
                </select>
              </div>
              <div className="assignment-actions">
                <button 
                  className="assign-btn"
                  onClick={handleAssignment}
                  disabled={!selectedAssignee || isAssigning || loadingEmployees}
                >
                  {isAssigning ? 'Assigning...' : 'Assign Bug'}
                </button>
                <button 
                  className="cancel-btn"
                  onClick={() => {
                    setShowAssignSection(false);
                    setSelectedAssignee('');
                  }}
                  disabled={isAssigning}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
        
        {bug.screenshots && bug.screenshots.length > 0 && (
          <div className="bug-screenshots">
            <h4>Screenshots</h4>
            <div className="screenshots-grid">
              {bug.screenshots.map((screenshot, index) => (
                <div key={index} className="screenshot-item">
                  <a href={screenshot.url} target="_blank" rel="noopener noreferrer">
                    <img src={screenshot.url} alt={`Screenshot ${index + 1}`} />
                  </a>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      
      <div className="bug-detail-footer">
        {isAssignedToCurrentUser && (
          <div className="status-actions">
            <span className="status-label">Update Status:</span>
            <button 
              className="status-button in-progress"
              onClick={() => onStatusUpdate('in_progress')}
              disabled={bug.status === 'in_progress'}
            >
              Mark In Progress
            </button>
            <button 
              className="status-button resolved"
              onClick={() => onStatusUpdate('resolved')}
              disabled={bug.status === 'resolved'}
            >
              Mark Resolved
            </button>
          </div>
        )}
        <button className="close-btn" onClick={onClose}>Close</button>
      </div>
    </div>
  );
};

export default BugDetailView; 